import React, { useState, useRef, useEffect } from 'react';
import {
  MoreHorizontal,
  CornerDownLeft,
  Forward,
  Copy,
  Pencil,
  CheckSquare,
  Pin,
  Trash2,
} from 'lucide-react';
import { cn } from '../../lib/utils';
import { useLanguage } from '../../contexts/LanguageContext';

export function MessageActionMenu({
  onReply,
  onForward,
  onCopy,
  onEdit,
  onSelect,
  onPin,
  onDelete,
  canDelete = true,
  isMe = false,
  isPinned = false,
  className,
}) {
  const { t } = useLanguage();
  const [open, setOpen] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  return (
    <div ref={menuRef} className="relative inline-flex items-center">
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          setOpen((p) => !p);
        }}
        className={cn(
          'flex h-6 w-6 items-center justify-center rounded-full text-zinc-400 hover:text-zinc-950 hover:bg-zinc-100 transition p-0.5',
          open && 'bg-zinc-100 text-zinc-950',
          className
        )}
        title={t('Veprime mbi mesazhin')}
      >
        <MoreHorizontal size={14} />
      </button>

      {open && (
        <div
          onClick={(e) => e.stopPropagation()}
          className={cn(
            'absolute bottom-8 z-[230] w-52 rounded-2xl border border-zinc-200 bg-white p-1.5 text-xs font-semibold text-zinc-900 shadow-lg animate-in zoom-in-95 duration-100',
            isMe ? 'right-0' : 'left-0'
          )}
        >
          <button
            type="button"
            onClick={() => {
              setOpen(false);
              onReply?.();
            }}
            className="flex w-full items-center gap-2 rounded-xl px-2.5 py-1.5 text-zinc-700 transition hover:bg-zinc-100"
          >
            <CornerDownLeft size={13} className="text-[#DD0000]" />
            <span>{t('Përgjigju')}</span>
          </button>

          {isMe && onEdit && (
            <button
              type="button"
              onClick={() => {
                setOpen(false);
                onEdit();
              }}
              className="flex w-full items-center gap-2 rounded-xl px-2.5 py-1.5 text-zinc-700 transition hover:bg-zinc-100"
            >
              <Pencil size={13} />
              <span>{t('Ndrysho')}</span>
            </button>
          )}

          <button
            type="button"
            onClick={() => {
              setOpen(false);
              onForward?.();
            }}
            className="flex w-full items-center gap-2 rounded-xl px-2.5 py-1.5 text-zinc-700 transition hover:bg-zinc-100"
          >
            <Forward size={13} className="text-[#DD0000]" />
            <span>{t('Përcill')}</span>
          </button>

          <button
            type="button"
            onClick={() => {
              setOpen(false);
              onCopy?.();
            }}
            className="flex w-full items-center gap-2 rounded-xl px-2.5 py-1.5 text-zinc-700 transition hover:bg-zinc-100"
          >
            <Copy size={13} />
            <span>{t('Kopjo')}</span>
          </button>

          {onSelect && (
            <button
              type="button"
              onClick={() => {
                setOpen(false);
                onSelect();
              }}
              className="flex w-full items-center gap-2 rounded-xl px-2.5 py-1.5 text-zinc-700 transition hover:bg-zinc-100"
            >
              <CheckSquare size={13} />
              <span>{t('Zgjidh')}</span>
            </button>
          )}

          {onPin && (
            <button
              type="button"
              onClick={() => {
                setOpen(false);
                onPin();
              }}
              className="flex w-full items-center gap-2 rounded-xl px-2.5 py-1.5 text-zinc-700 transition hover:bg-zinc-100"
            >
              <Pin size={13} />
              <span>{t(isPinned ? 'Hiqe fiksimin' : 'Fikso mesazhin')}</span>
            </button>
          )}

          {canDelete && (
            <>
              <div className="my-1 border-t border-zinc-200" />
              <button
                type="button"
                onClick={() => {
                  setOpen(false);
                  onDelete?.();
                }}
                className="flex w-full items-center gap-2 rounded-xl px-2.5 py-1.5 text-red-600 transition hover:bg-red-50"
              >
                <Trash2 size={13} />
                <span>{t('Fshi')}</span>
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
}

export default MessageActionMenu;
